import {
  layoutOverhead,
  OVERHEAD_LAYOUT_DEFAULTS,
  type OverheadItem,
  type OverheadLayoutConfig,
  type OverheadPlacement,
} from './layoutOverhead';
import { NAMEPLATE } from '../../../shared/config/nameplate.config';
import { getOverheadNamesMode, type OverheadNamesMode } from './overheadNamesPref';

/**
 * The overhead layout pass (plan 15 §2.1, phase 3): one registry every
 * overhead owner reports its label group to, and reads its placement from.
 *
 * - Owners `report()` their item every frame they draw it (mobs, players,
 *   NPCs) and `read()` a placement — the eased offset and name alpha to apply
 *   with `setPosition` / `setAlpha`. Nothing here touches Pixi.
 * - `endFrame()` (once per frame, from MobManager.update) drops items not
 *   reported for two frames and re-solves ONLY when the inputs changed: an
 *   item entered or left, its tier or box size changed, or it moved more than
 *   `moveQuantumPx` since the last solve — plus a `maxIntervalMs` safety net.
 *   Between solves each owner applies its cached nudge to its live anchor.
 * - Offsets and alphas ease toward the solve over `easeMs`, so a label never
 *   jumps a row in one frame.
 */
export interface OverheadRuntimeConfig extends OverheadLayoutConfig {
  enabled: boolean;
  moveQuantumPx: number;
  maxIntervalMs: number;
  easeMs: number;
}

export const OVERHEAD_RUNTIME_DEFAULTS: OverheadRuntimeConfig = {
  ...OVERHEAD_LAYOUT_DEFAULTS,
  enabled: NAMEPLATE.declutter.enabled,
  moveQuantumPx: NAMEPLATE.declutter.moveQuantumPx,
  maxIntervalMs: NAMEPLATE.declutter.maxIntervalMs,
  easeMs: NAMEPLATE.declutter.easeMs,
};

interface Entry {
  item: OverheadItem;
  /** Frame the item was last reported in. */
  seen: number;
  /** Item as of the last solve (change detection). */
  solvedAs: OverheadItem | null;
  offset: number;
  alpha: number;
  barAlpha: number;
  lastReadMs: number;
}

const NO_PLACEMENT: OverheadPlacement = { offsetY: 0, nameAlpha: 1 };

export class OverheadLayout {
  private static instance: OverheadLayout | null = null;
  static get(): OverheadLayout {
    return (this.instance ??= new OverheadLayout());
  }
  /** Test hook. */
  static reset(cfg?: Partial<OverheadRuntimeConfig>): OverheadLayout {
    this.instance = new OverheadLayout({ ...OVERHEAD_RUNTIME_DEFAULTS, ...cfg });
    return this.instance;
  }

  private entries = new Map<string, Entry>();
  private solved = new Map<string, OverheadPlacement>();
  private frame = 0;
  private dirty = false;
  private mode: OverheadNamesMode = 'smart';
  /** Tests pin the names mode; the game reads the user pref. */
  modeOverride: OverheadNamesMode | null = null;
  private lastSolveMs = -Infinity;
  /** Solves run (diagnostics + tests). */
  solveCount = 0;

  /** Frame counter (advanced by endFrame) — keys the per-frame tier context. */
  get frameNumber(): number {
    return this.frame;
  }

  constructor(private readonly cfg: OverheadRuntimeConfig = OVERHEAD_RUNTIME_DEFAULTS) {}

  report(item: OverheadItem): void {
    const e = this.entries.get(item.id);
    if (!e) {
      this.entries.set(item.id, { item, seen: this.frame, solvedAs: null, offset: 0, alpha: 1, barAlpha: 1, lastReadMs: -1 });
      this.dirty = true;
      return;
    }
    e.item = item;
    e.seen = this.frame;
    if (!this.dirty && e.solvedAs && this.changed(e.solvedAs, item)) this.dirty = true;
  }

  /** Eased placement for `id` at `nowMs`; natural spot when not laid out
   *  (or when the layout is switched off: NAMEPLATE.declutter.enabled). */
  read(id: string, nowMs: number): OverheadPlacement {
    const e = this.entries.get(id);
    if (!e || !this.cfg.enabled) return NO_PLACEMENT;
    const solved = this.solved.get(id) ?? NO_PLACEMENT;
    const target = { offsetY: solved.offsetY, nameAlpha: solved.nameAlpha * (e.item.distanceAlpha ?? 1) };
    const dt = e.lastReadMs < 0 ? Infinity : Math.max(0, nowMs - e.lastReadMs);
    e.lastReadMs = nowMs;
    const k = this.cfg.easeMs <= 0 ? 1 : Math.min(1, dt / this.cfg.easeMs);
    e.offset += (target.offsetY - e.offset) * k;
    e.alpha += (target.nameAlpha - e.alpha) * k;
    const bar = solved.barAlpha ?? 1;
    e.barAlpha += (bar - e.barAlpha) * k;
    if (Math.abs(e.offset - target.offsetY) < 0.25) e.offset = target.offsetY;
    if (Math.abs(e.alpha - target.nameAlpha) < 0.01) e.alpha = target.nameAlpha;
    if (Math.abs(e.barAlpha - bar) < 0.01) e.barAlpha = bar;
    return { offsetY: e.offset, nameAlpha: e.alpha, barAlpha: e.barAlpha };
  }

  /**
   * World y of `id`'s group top as last drawn (its reported top raised by the
   * current eased nudge), or null when the group isn't laid out. Read-only:
   * chat bubbles sit above it (lane AG). Doesn't advance the ease.
   */
  groupTop(id: string): number | null {
    const e = this.entries.get(id);
    if (!e) return null;
    return e.item.top - (this.cfg.enabled ? e.offset : 0);
  }

  /** The solved (un-eased) placement, for tests and diagnostics. */
  solvedPlacement(id: string): OverheadPlacement | null {
    return this.solved.get(id) ?? null;
  }

  remove(id: string): void {
    if (this.entries.delete(id)) this.dirty = true;
    this.solved.delete(id);
  }

  /** Once per frame: drop stale items, re-solve if anything changed. */
  endFrame(nowMs: number): void {
    for (const [id, e] of this.entries) {
      if (this.frame - e.seen > 1) {
        this.entries.delete(id);
        this.solved.delete(id);
        this.dirty = true;
      }
    }
    const mode = this.modeOverride ?? getOverheadNamesMode();
    if (mode !== this.mode) { this.mode = mode; this.dirty = true; }
    if (this.dirty || nowMs - this.lastSolveMs >= this.cfg.maxIntervalMs) this.solve(nowMs);
    this.frame++;
  }

  private solve(nowMs: number): void {
    const items: OverheadItem[] = [];
    for (const e of this.entries.values()) items.push(e.item);
    this.solved = layoutOverhead(items, this.solved, { ...this.cfg, mode: this.mode });
    for (const e of this.entries.values()) e.solvedAs = e.item;
    this.dirty = false;
    this.lastSolveMs = nowMs;
    this.solveCount++;
  }

  private changed(a: OverheadItem, b: OverheadItem): boolean {
    const q = this.cfg.moveQuantumPx;
    return a.tier !== b.tier
      || a.kind !== b.kind
      || !!a.damaged !== !!b.damaged
      || Math.abs(a.x - b.x) > q
      || Math.abs(a.bottom - b.bottom) > q
      || Math.abs((a.bottom - a.top) - (b.bottom - b.top)) > 0.5
      || Math.abs(a.width - b.width) > 0.5;
  }
}
